import { useMultipleSelection, useCombobox } from 'downshift';
import React, { useMemo, useCallback, useEffect } from 'react';
import { v4 } from 'uuid';
import { classNames } from '../../utils/classNames/classNames.esm.js';
import { mergeRefs } from '../../utils/refs/refs.esm.js';
import { useOptionallyControlledState } from '../../utils/useOptionallyControlledState.esm.js';
import { usePopper } from '../../utils/usePopper.esm.js';
import { Portal } from '../Portal/Portal.esm.js';
import '../../utils/constants.esm.js';
import '../Button/Button.esm.js';
import '@panwds/icons';
import '../Icon/ImgIcon.esm.js';
import '../Icon/SemanticIcons.esm.js';
import '../Button/ProgressButton.esm.js';
import '../Button/CopyButton.esm.js';
import '@headlessui/react';
import { SelectList } from '../Select/SelectList.esm.js';
import 'react-intl';
import '../Input/Input.esm.js';
import { Tag } from '../Tag/Tag.esm.js';

const defaultItemToString = (item) => String(item.value);
const defaultFilterFn = (filter, value) => value.toLowerCase().includes(filter.toLowerCase());
function ChipInput({ allowArbitraryItems = false, "data-autofocus": dataAutofocus, dataMetrics, dataTestId, defaultPlacement = "bottom-start", disabled, filterFn = defaultFilterFn, inputRef: userInputRef, inputValue: userInputValue, isLoading, items, itemToString = defaultItemToString, labelId, menuStyle, onInputBlur, onInputValueChange, onSelectedItemsChange, selectedItems = [], }) {
    const baseId = useMemo(() => v4(), []);
    const [inputValue, handleInputValueChange] = useOptionallyControlledState({
        currentValue: userInputValue,
        defaultValue: "",
        onChange: onInputValueChange,
    });
    const { getSelectedItemProps, getDropdownProps, addSelectedItem, removeSelectedItem, } = useMultipleSelection({
        itemToString,
        onSelectedItemsChange,
        selectedItems,
    });
    const filteredItems = useMemo(() => {
        const base = allowArbitraryItems && inputValue ? [{ value: inputValue }] : [];
        const unselectedItems = base.concat(items.filter((item) => !selectedItems.some((selectedItem) => selectedItem.value === item.value)));
        if (onInputValueChange)
            return unselectedItems;
        return unselectedItems.filter((item) => filterFn(inputValue, itemToString(item)));
    }, [
        allowArbitraryItems,
        items,
        inputValue,
        onInputValueChange,
        selectedItems,
    ]);
    /**
     * This function handles item select event.
     * If current item is not added, add it.
     */
    const handleSelect = useCallback((selectedItem) => {
        if (selectedItem) {
            handleInputValueChange("");
            const duplicates = selectedItems.filter(({ value }) => selectedItem.value === value);
            if (duplicates.length === 0) {
                addSelectedItem(selectedItem);
            }
        }
    }, [selectedItems, addSelectedItem, handleInputValueChange]);
    const { isOpen, getMenuProps, getInputProps, getComboboxProps, highlightedIndex, getItemProps, openMenu, setHighlightedIndex, } = useCombobox({
        defaultHighlightedIndex: 0,
        inputValue,
        id: baseId,
        items: filteredItems,
        labelId,
        stateReducer: (_state, actionAndChanges) => {
            const { changes, type } = actionAndChanges;
            switch (type) {
                case useCombobox.stateChangeTypes.InputKeyDownEnter:
                case useCombobox.stateChangeTypes.ItemClick: {
                    return {
                        ...changes,
                        isOpen: true, // keep the menu open after selection.
                    };
                }
            }
            return changes;
        },
        onStateChange: ({ type, selectedItem }) => {
            switch (type) {
                /**
                 * Handle adding by mouse click only.
                 * Key events are handled in custom key events.
                 */
                case useCombobox.stateChangeTypes.ItemClick:
                    handleSelect(selectedItem);
                    break;
            }
        },
    });
    /**
     * Implement custom key events
     * When Tab/Enter key is pressed, add the currently selected item.
     * When Backspace is pressed and user typed nothing, remove last added item.
     */
    const handleKeyDown = useCallback((event) => {
        var _a;
        if (event.key === "Tab" || event.key === "Enter") {
            if (highlightedIndex >= 0 &&
                !((_a = filteredItems[highlightedIndex]) === null || _a === void 0 ? void 0 : _a.disabled)) {
                handleSelect(filteredItems[highlightedIndex]);
            }
        }
        else if (event.key === "Backspace") {
            if (selectedItems.length > 0 && !inputValue) {
                const lastItem = selectedItems[selectedItems.length - 1];
                if (!(lastItem === null || lastItem === void 0 ? void 0 : lastItem.disabled)) {
                    removeSelectedItem(lastItem);
                }
            }
        }
        else {
            return;
        }
        setTimeout(() => {
            event.target.focus();
        }, 0);
    }, [highlightedIndex, handleSelect, filteredItems, inputValue, selectedItems]);
    useEffect(() => {
        var _a;
        if ((_a = filteredItems[highlightedIndex]) === null || _a === void 0 ? void 0 : _a.disabled) {
            const nextHighlightedIndex = filteredItems.findIndex((item) => !item.disabled);
            setHighlightedIndex(nextHighlightedIndex);
        }
    }, [highlightedIndex, filteredItems, setHighlightedIndex]);
    const { triggerRef, containerRef } = usePopper({
        placement: defaultPlacement,
        strategy: "fixed",
    });
    const inputRef = userInputRef
        ? mergeRefs([triggerRef, userInputRef])
        : triggerRef;
    const inputClassName = classNames("tw-box-border tw-border-none focus:tw-outline-none tw-placeholder-gray-300 tw-text-xs tw-bg-transparent tw-w-full", disabled && "tw-cursor-not-allowed");
    const rootClassName = classNames("tw-box-border tw-border-solid tw-border-gray-200 tw-flex tw-flex-wrap tw-items-center tw-gap-x-1 tw-gap-y-1 tw-px-2 tw-border tw-rounded focus-within:tw-custom-ring dark:tw-bg-gray-1000 dark:tw-border-gray-700", disabled &&
        "tw-text-disabled tw-cursor-not-allowed dark:tw-border-gray-700 dark:tw-text-dark-bg-disabled");
    return (React.createElement(React.Fragment, null,
        React.createElement("div", { "data-metrics": dataMetrics, "data-testid": dataTestId || "chip-input", className: rootClassName, style: {
                paddingBottom: "0.1875rem",
                paddingTop: "0.1875rem",
                minHeight: "2rem",
            } },
            selectedItems.map((selectedItem, index) => {
                if (disabled) {
                    return (React.createElement(Tag, { key: index, dataTestId: "selected-item", icon: selectedItem.iconLeft }, selectedItem.value));
                }
                return (React.createElement(Tag, { dataTestId: "selected-item", key: index, appearance: selectedItem.appearance, icon: selectedItem.iconLeft, closeButtonProps: {
                        ...getSelectedItemProps({
                            selectedItem,
                            index,
                        }),
                        // Note that we are overriding, not extending, the onClick from downshift
                        // The onClick from downshift would apply focus to the Tag
                        // Instead, we remove it (because the click is on a remove button)
                        onClick() {
                            removeSelectedItem(selectedItem);
                        },
                    } }, selectedItem.value));
            }),
            React.createElement("span", { className: "tw-flex-grow", ...getComboboxProps() },
                React.createElement("input", { className: inputClassName, "data-autofocus": dataAutofocus, ...getInputProps({
                        ...getDropdownProps({
                            disabled,
                            onBlur: onInputBlur,
                            onChange(e) {
                                handleInputValueChange(e.currentTarget.value);
                            },
                            onFocus() {
                                if (!isOpen) {
                                    openMenu();
                                }
                            },
                            preventKeyAction: !!inputValue,
                            ref: inputRef,
                            value: inputValue,
                        }),
                        onKeyDown: handleKeyDown,
                    }) }))),
        React.createElement(Portal, null,
            React.createElement("div", { className: "tw-z-30", ref: containerRef },
                React.createElement(SelectList, { getMenuProps: getMenuProps, getItemProps: getItemProps, highlightedIndex: highlightedIndex, isLoading: isLoading, isOpen: isOpen, items: filteredItems, menuStyle: menuStyle, showSelectAll: false, selectAllItem: { value: "select all" }, selectedItems: [], selectedValues: [], emptyState: allowArbitraryItems && filteredItems.length === 0 ? "" : undefined })))));
}

export { ChipInput };
//# sourceMappingURL=ChipInput.esm.js.map
